import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Building2, LogOut, Search, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { IconComponent } from '../../util/icons';
import { formatRole } from '../../util/role-display';
import { useOrgSelection } from './hooks/useOrgSelection';
import { getAllOrganisation } from '../../services/org-data';
import type { AccessibleOrg } from '../../types/org-selection.types';

import logo from '../../asset/images/logo.png';

type OrgItem = AccessibleOrg;

const OrganizationSelectionPage = () => {
  const { isLoading, selectOrg, endSession, fetchOrgData } = useOrgSelection();
  // Seeded from the cache on the FIRST render, not in the mount effect: an
  // empty initial list painted "No Organizations Available" for one frame to
  // every user before the effect swapped the cards in (PR #1152 review,
  // 2026-09-16). The effect below still owns the refetch for a cache miss.
  const [orgList, setOrgList] = useState<OrgItem[]>(getAllOrganisation);
  const [searchTerm, setSearchTerm] = useState('');
  // Distinguishes "the fallback fetch failed" from "this user has zero
  // accounts". Both render no cards, and only one of them is the user's fault.
  const [loadFailed, setLoadFailed] = useState(false);
  // Whether the list has been answered at all — by the cache, or by a fetch
  // that has come back either way. The zero-accounts empty state must wait
  // for this; an unanswered list is not an empty one.
  const [listSettled, setListSettled] = useState(() => getAllOrganisation().length > 0);
  // The hook rebuilds `fetchOrgData` every render; a ref keeps the dependency
  // lists honest instead of re-running the effect per render.
  const fetchOrgDataRef = useRef(fetchOrgData);
  fetchOrgDataRef.current = fetchOrgData;

  const loadOrgList = useCallback(async () => {
    setLoadFailed(false);
    const result = await fetchOrgDataRef.current();
    setListSettled(true);
    if (result.kind !== 'customer') {
      // Only 'error' is actually reachable — `fetchOrgData` has not returned
      // the 'admin' branch since DV-2958 removed it — but neither is a list
      // this page can render, and persisting either would cache the problem
      // into `OrgData` for every later reader. `accessibleOrgs()` writes only
      // after a fetch that did not throw; this matches it.
      setLoadFailed(true);
      return;
    }
    localStorage.setItem('OrgData', JSON.stringify(result.data));
    setOrgList(result.data);
  }, []);

  useEffect(() => {
    document.title = 'PlanOmatic - Accounts';

    // Plain UI since DV-3012: the bootstrap has already tried a deep link and
    // the single-org case, so reaching this page means the user genuinely has
    // a choice to make.
    //
    // `OrgData` was written by whatever routed the user here, and a reload
    // reuses that write — which is why the old
    // `performance.getEntriesByType('navigation')` refresh-sniffing is gone
    // (spec §4.1). It is NOT guaranteed fresh by this route:
    // `/select-organization` is `entity: 'none'`, so `resolveEntityScope`
    // returns `ready` before it would ever call `accessibleOrgs()`.
    //
    // One entry can land here with no list at all: when `accessibleOrgs()`
    // throws, the step's catch assigns `initiateOrgSwitch()` — which routes
    // here via `unscopedLandingRoute()` — without having written anything. Without the refetch below that is an empty account chooser
    // with no way forward. Deliberately conditional — an unconditional refetch
    // would take back the "the picker fetches its own data" job the step now
    // owns, and put a second copy of the persist beside `accessibleOrgs()`'s.
    if (getAllOrganisation().length === 0) {
      void loadOrgList();
    }

    return () => {
      document.title = 'PlanOmatic';
    };
  }, [loadOrgList]);

  const filteredOrgs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orgList;
    return orgList.filter((org) => org.name.toLowerCase().includes(term));
  }, [orgList, searchTerm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  const showNoMatch = searchTerm.trim().length > 0 && filteredOrgs.length === 0;
  // A SUCCESSFUL fetch that returned nothing — not the same as `loadFailed`,
  // and not the same as a search that matched nothing. This page used to
  // render its welcome heading over an empty list, with Sign Out as the only
  // control and no explanation (spec §4, close-out §5).
  const showNoOrgs = listSettled && !loadFailed && orgList.length === 0;

  return (
    <section className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4">
        <IconComponent src={logo} alt="PlanOmatic" width="184px" height="43px" />
        <Button variant="ghost" onClick={endSession}>
          <LogOut className="mr-1 h-5 w-5" />
          Sign Out
        </Button>
      </header>

      <main>
        <div className="mx-auto w-full max-w-7xl px-6 pb-12 pt-10">
          <div className="text-center">
            <Typography
              as="h1"
              variant="heading-xl"
              className="lg:text-[32px] lg:leading-10 xl:text-[36px] xl:leading-[44px] 2xl:text-[40px] 2xl:leading-[48px]"
            >
              Welcome back {localStorage.getItem('userName')}
            </Typography>
            <Typography
              as="p"
              variant="body"
              className="mt-2 text-muted-foreground lg:text-base lg:leading-6 xl:text-lg xl:leading-7"
            >
              You are associated with multiple organizations. Choose an account to proceed.
            </Typography>
          </div>

          {loadFailed && (
            <EmptyState
              icon={AlertCircle}
              title="Couldn't Load Your Organizations"
              description="Something went wrong reaching your accounts. This is not a problem with your login — check your connection and try again."
              className="mt-10 h-auto py-12"
              action={<Button onClick={() => void loadOrgList()}>Try Again</Button>}
            />
          )}

          {!loadFailed && orgList.length > 1 && (
            <div className="relative mx-auto mt-8 max-w-md">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations"
                className="pl-9"
                aria-label="Search organizations"
              />
            </div>
          )}

          {!loadFailed &&
            (showNoOrgs ? (
              <EmptyState
                icon={Building2}
                title="No Organizations Available"
                description="Your account isn't associated with any organizations yet. Contact your administrator to get access."
                className="mt-10 h-auto py-12"
              />
            ) : showNoMatch ? (
              <EmptyState
                icon={SearchX}
                title="No matching organizations"
                description={`No organizations match "${searchTerm}".`}
                className="mt-10 h-auto py-12"
              />
            ) : (
              <ul
                role="list"
                className="mt-8 flex flex-wrap justify-center gap-4"
              >
                {filteredOrgs.map((item) => (
                  <OrgCard key={item.entityId} item={item} onSelect={selectOrg} />
                ))}
              </ul>
            ))}
        </div>
      </main>
    </section>
  );
};

interface OrgCardProps {
  item: OrgItem;
  onSelect: (companyName: string, role: string, entityId: string) => void;
}

const OrgCard = ({ item, onSelect }: OrgCardProps) => {
  const roleLabel = formatRole(item.role);
  return (
    <li className="w-full sm:w-[240px] lg:w-[280px] xl:w-[320px] 2xl:w-[360px]">
      <button
        type="button"
        disabled={item.disabled}
        onClick={() => onSelect(item.name, item.role, item.entityId)}
        className={cn(
          'flex h-full w-full flex-col items-center gap-3 rounded-lg border border-border bg-background p-6 text-center transition-all lg:gap-4 lg:p-8 2xl:p-10',
          'hover:border-primary/40 hover:shadow-md',
          item.disabled &&
            'cursor-not-allowed opacity-60 hover:border-border hover:shadow-none',
        )}
      >
        <OrgAvatar logo={item.logo} company={item.name} />
        <Typography
          as="div"
          variant="body-semibold"
          className="line-clamp-2 min-h-[2.5rem] lg:min-h-[3rem] lg:text-base lg:leading-6 xl:min-h-[3.5rem] xl:text-lg xl:leading-7"
        >
          {item.name}
        </Typography>
        <Typography
          variant="small-medium"
          className="text-muted-foreground lg:text-sm lg:leading-5"
        >
          {roleLabel}
        </Typography>
        {item.disabled && (
          <div className="mt-auto flex items-center gap-1 pt-1">
            <AlertCircle
              aria-hidden="true"
              className="h-4 w-4 text-destructive lg:h-[18px] lg:w-[18px]"
            />
            <Typography
              variant="small-medium"
              className="text-destructive lg:text-sm lg:leading-5"
            >
              Account Disabled
            </Typography>
          </div>
        )}
      </button>
    </li>
  );
};

const AVATAR_SIZE_CLASSES = 'h-20 w-20 xl:h-24 xl:w-24 2xl:h-28 2xl:w-28';

const OrgAvatar = ({ logo, company }: { logo?: string; company: string }) => {
  if (logo) {
    return (
      <img
        src={logo}
        alt={`${company} logo`}
        width={80}
        height={80}
        className={cn(
          AVATAR_SIZE_CLASSES,
          'rounded-full border border-border object-cover',
        )}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={cn(
        AVATAR_SIZE_CLASSES,
        'flex items-center justify-center rounded-full border border-border bg-muted text-muted-foreground',
      )}
    >
      <Building2 className="h-8 w-8 xl:h-10 xl:w-10 2xl:h-12 2xl:w-12" />
    </div>
  );
};

export default OrganizationSelectionPage;
